import * as React from 'react';
import { observer } from 'mobx-react';
import { Modal, ModalChildStyle } from '../modal';
import { classes, media, style } from 'typestyle';
import { em, percent, px, rgba } from 'csx';
import overlayStyles, { ScrollableOverlayCloseButtonStyle } from '../../styles/scrollable-overlay';
import { ButtonPill } from '../button-pill';
import { center, centerCenter, endJustified, horizontal, horizontallySpaced, vertical } from 'csstips';
import { ChannelAvatar } from '../channel-avatar';
import { ChannelSchema } from '../../types/schema';
import GenericError from '../generic-error';
import { SVGIcon } from '../svg-icon';
import {
  colorBlack,
  colorBlueGrey,
  colorBrand,
  colorDarkGreen,
  colorLightBlueGrey,
  colorLightGrey,
  colorSlate,
  colorStatusWarning,
  colorWhite,
} from '../../theme/color';
import { Loading } from '../loading/loading';
import GoogleButton from '../user/google-button';
import { fontTitles } from '../../theme/font';
import ChannelListing from './channel-listing';
import { ModalModel } from '../../modules/modal/model';
import { mediaTablet } from '../../theme/media';
import { constantTabletBreakpoint } from '../../theme/constant';

export interface MigrationChannelModalProps {
  model: ModalModel['migrationChannel'];
  show: boolean;
  onRequestClose(): void;
  i18n?: {
    title: string;
    active: string;
    loading: string;
    activating: string;
    choose: string;
    close: string;
    add: string;
  };
}

@observer
export class MigrationChannelModal extends React.Component<MigrationChannelModalProps, any> {
  render() {
    const { onRequestClose, model, show, i18n = MigrationChannelModal.i18n } = this.props;
    const { loading, selecting, addUrl } = model;

    const header = (
      <header className={MigrationChannelModal.styles.header}>
        <h3>Your channels</h3>
        <button onClick={onRequestClose} className={MigrationChannelModal.styles.button}>
          <SVGIcon.Cross size={16} color="black" />
        </button>
      </header>
    );

    if (!show) return <Modal show={false} onRequestClose={void 0} />;

    if (loading)
      return (
        <Modal show={true} onRequestClose={onRequestClose}>
          <section className={MigrationChannelModal.styles.container}>
            {header}
            <Loading className={MigrationChannelModal.styles.loading} text="Loading channels..." />
          </section>
        </Modal>
      );

    return (
      <Modal show={true} onRequestClose={selecting ? void 0 : onRequestClose}>
        <section className={MigrationChannelModal.styles.container}>
          {header}
          <main className={MigrationChannelModal.styles.main}>
            <div className={MigrationChannelModal.styles.messageBox}>
              <SVGIcon.InfoCircle color={colorStatusWarning.toHexString()} />
              <p className={MigrationChannelModal.styles.message}>
                We’ve made some updates to the way YouTube channels are managed.
              </p>
              <p className={MigrationChannelModal.styles.message}>Please review and add any missing channels below.</p>
            </div>
            {!model.channels.length ? (
              <GenericError />
            ) : (
              <ChannelListing channels={model.channels} selectedChannel={model.channel} />
            )}
          </main>
          <footer className={MigrationChannelModal.styles.footer}>
            <h4 className={MigrationChannelModal.styles.footerTitle}>Need to add a channel?</h4>
            <GoogleButton
              href={addUrl}
              text="Connect with Google"
              className={MigrationChannelModal.styles.addButton}
              onClick={onRequestClose}
            />
            <div className={MigrationChannelModal.styles.authority}>
              <SVGIcon.YouTubeCertified color={colorBlack.toHexString()} />
            </div>
            <a className={MigrationChannelModal.styles.legal} href="/terms-and-conditions">
              Terms and conditions
            </a>
            <a className={MigrationChannelModal.styles.legal} href="/privacy-policy">
              Privacy policy
            </a>
          </footer>
        </section>
      </Modal>
    );
  }

  static i18n = {
    title: 'Switch channel',
    active: 'Current',
    choose: 'Choose',
    loading: 'Loading channels...',
    activating: 'Activating...',
    close: 'Cancel',
    add: 'Add a channel',
  };

  static styles = {
    container: style({
      ...ModalChildStyle,
      maxWidth: px(350),
      width: percent(100),
      padding: 0,
      backgroundColor: colorWhite.toHexString(),
    }),
    loading: style({ margin: '0 100px' }),
    header: style({
      position: 'relative',
      textAlign: 'center',
      $nest: {
        '& h3': {
          fontSize: px(16),
          lineHeight: px(16),
        },
      },
    }),
    button: style(ScrollableOverlayCloseButtonStyle),
    messageBox: style(vertical, {
      padding: px(16),
      margin: px(8),
      border: `1px solid ${colorStatusWarning.toHexString()}`,
      borderRadius: px(8),
      textAlign: 'center',
      alignItems: 'center',
    }),
    message: style({ fontSize: px(12), margin: '4px 0 0' }),
    main: style({
      // padding: ModalChildStyle.padding,
    }),
    buttons: style({
      ...horizontal,
      ...horizontallySpaced(10),
      ...endJustified,
      margin: '30px 0 0 0',
    }),
    footer: style({
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      paddingBottom: px(16),
      $nest: {
        '& h4': {
          fontSize: px(13),
        },
      },
    }),
    footerTitle: style({
      margin: '16px 0 8px',
    }),
    addButton: style({
      fontSize: px(13),
    }),
    dismissButton: style({
      margin: '16px 0',
    }),
    authority: style({
      ...centerCenter,
      ...horizontal,
      ...horizontallySpaced(30),
      margin: '16px 0',
      $nest: {
        '& path': {
          fill: colorSlate.toString(),
        },
        '& path:nth-child(4)': {
          fill: colorWhite.toString(),
        },
      },
    }),
    legal: style({
      fontSize: px(12),
      textDecoration: 'underline',
    }),
  };
}
